// apps/provider/src/components/video/VideoLaunchButton.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Loader, AlertCircle, Clock } from 'lucide-react';
import { apiService } from '../../services/api';
import { Button } from '../ui/Button';
import { useUser } from '../../contexts/UserContext';
import { createTimestamp } from '@prism-ehr/shared';

const VideoLaunchButton = ({ appointment, size = 'default', className = '', onUpdate }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [videoSession, setVideoSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [encounter, setEncounter] = useState(null);
  
  useEffect(() => {
    checkVideoSessionAndEncounter();
  }, [appointment.id]);
  
  const checkVideoSessionAndEncounter = async () => {
    try {
      setChecking(true);
      
      // Check for existing video session
      const session = await apiService.getVideoSessionByAppointment(appointment.id);
      if (session && session.length > 0) {
        setVideoSession(Array.isArray(session) ? session[0] : session);
      }
      
      // Check for existing encounter
      if (appointment.encounter_id) {
        const encounterData = await apiService.getEncounter(appointment.encounter_id);
        setEncounter(encounterData);
      }
    } catch (err) {
      console.error('Error checking session/encounter:', err);
    } finally {
      setChecking(false);
    }
  };
  
  const handleStartVideo = async () => {
    setLoading(true);
    try {
      let encounterId = appointment.encounter_id || encounter?.id;
      
      // Only create encounter if one doesn't exist
      if (!encounterId) {
        const encounterData = {
          patient_id: appointment.patient_id,
          provider_id: appointment.provider_id,
          organization_id: user.organization_id,
          appointment_id: appointment.id,
          encounter_date: createTimestamp(appointment.appointment_date, appointment.start_time),
          encounter_type: appointment.appointment_type,
          status: 'in_progress',
          reason_for_visit: appointment.visit_reason,
          location: 'Telehealth',
          location_id: appointment.location_id,
          is_telehealth: true,
          video_initiated_at: new Date().toISOString(),
          chief_complaint: appointment.chief_complaint || appointment.visit_reason,
          documentation_status: 'pending'
        };

        try {
          const newEncounter = await apiService.createEncounter(encounterData);
          encounterId = newEncounter.id;
          
          // Update appointment with encounter reference and status
          await apiService.updateAppointment(appointment.id, {
            encounter_id: encounterId,
            status: 'in_progress'
          });
          
          // Call the onUpdate callback if provided to refresh parent component
          if (onUpdate) {
            await onUpdate();
          }
        } catch (encounterError) {
          console.error('Error creating encounter:', encounterError);
          alert('Failed to create encounter for video visit. Please try again.');
          setLoading(false);
          return;
        }
      }
      
      // Navigate to video room with encounter context
      navigate(`/appointments/${appointment.id}/video`, {
        state: { encounterId }
      });
    } catch (err) {
      console.error('Error starting video:', err);
      alert('Failed to start video session. Please try again.');
      setLoading(false);
    }
  };
  
  if (checking) {
    return (
      <Button size={size} disabled className={className}>
        <Loader className="h-4 w-4 mr-2 animate-spin" />
        Checking...
      </Button>
    );
  }
  
  if (!appointment.is_telehealth) {
    return null;
  }
  
  // Check if appointment status makes it ineligible
  if (appointment.status === 'cancelled' || appointment.status === 'completed' || appointment.status === 'no_show') {
    return (
      <Button size={size} disabled className={`px-5 py-2.5 ${className}`}>
        <Video className="h-4 w-4 mr-2" />
        Video Unavailable
      </Button>
    );
  }
  
  // Different button states based on session/encounter status
  if (videoSession && videoSession.status === 'active' && encounter) {
    return (
      <Button 
        size={size}
        onClick={handleStartVideo}
        className={`bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 ${className}`}
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader className="h-4 w-4 mr-2 animate-spin" />
            Rejoining...
          </>
        ) : (
          <>
            <Video className="h-4 w-4 mr-2" />
            Rejoin Video Visit
          </>
        )}
      </Button>
    );
  }
  
  return (
    <Button 
      size={size}
      onClick={handleStartVideo}
      className={`bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 ${className}`}
      disabled={loading}
    >
      {loading ? (
        <>
          <Loader className="h-4 w-4 mr-2 animate-spin" />
          Starting...
        </>
      ) : encounter ? (
        <>
          <Video className="h-4 w-4 mr-2" />
          Resume Video Visit
        </>
      ) : (
        <>
          <Video className="h-4 w-4 mr-2" />
          Start Video Visit
        </>
      )}
    </Button>
  );
};

export default VideoLaunchButton;